import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlassian/wac-ufo-segment";

import Image from "@/components/wpl/image/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import {
    BGProps,
    Row,
    StyledBackground
} from "@/libraries/wpl/foundations/src";
import CTAPrimary from "@/patterns/wpl/cta-primary/src";

const backgroundProperties: BGProps = {
    partialBackgrounds: [
        {
            imageLink:
                "https://wac-cdn.atlassian.com/dam/jcr:d281b49a-9812-46dd-996c-e35789461eec/CareersRight_540x450.svg?cdnVersion=1221",
            positionDesktop: "cornerBottomRight",
            positionTablet: "cornerBottomRight",
            positionMobile: "hidden"
        },
        {
            imageLink:
                "https://wac-cdn.atlassian.com/dam/jcr:76ed2bec-4e88-4341-b0ce-83ede8e68015/CareersLeft_540x450.svg?cdnVersion=1221",
            positionDesktop: "cornerBottomLeft",
            positionTablet: "cornerBottomLeft",
            positionMobile: "hidden"
        }
    ]
};

// style overrides to match existing WAC home-page
const careersStyle = css({
    ".styled-background": {
        [media.only.sm]: {
            backgroundPosition: "bottom left -200px, bottom right -200px",
            backgroundSize: "contain, contain"
        },
        [media.above.md]: {
            backgroundSize: "contain, contain"
        },
        [media.only.md]: {
            backgroundPosition: "bottom left -150px, bottom right -150px"
        }
    },

    "paddingBlockStart": "64px",
    ".cta-primary": {
        [media.above.sm]: {
            paddingBlockEnd: "64px",
            marginInlineStart: "auto",
            marginInlineEnd: "auto",
            maxWidth: "460px"
        },
        paddingBlockEnd: 0,
        paddingBlockStart: "8px"
    },
    "h3.contentBlock-eyebrow": {
        [media.above.md]: {
            marginBlockEnd: "16px"
        },
        color: "#2684ff",
        fontFamily:
            "Charlie Text, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Noto Sans, Ubuntu, Droid Sans, Helvetica Neue, sans-serif",
        fontSize: "16px",
        fontWeight: 800,
        letterSpacing: "1.6px",
        lineHeight: 2,
        marginBlockEnd: "10px",
        WebkitFontSmoothing: "antialiased"
    },
    ".primary-cta-content": {
        "h2": {
            color: "#253858",
            fontSize: "2rem",
            letterSpacing: 0,
            marginBlockStart: 0,
            lineHeight: 1.25,
            marginBlockEnd: "14px",
            [media.only.sm]: {
                fontSize: "2.75rem",
                lineHeight: 1.18181818,
                marginBlockEnd: "16px"
            },
            [media.only.md]: {
                fontSize: "2.75rem",
                lineHeight: 1.18181818,
                marginBlockEnd: "16px"
            },
            [media.above.lg]: {
                fontSize: "3rem",
                lineHeight: "3.5rem",
                marginBlockEnd: "24px"
            }
        },
        ".contentBlock-body": {
            fontFamily:
                '"Charlie Display", -apple-system, "system-ui", "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif',
            fontSize: "1.25rem",
            letterSpacing: "0.3px",
            color: "#42526e",
            marginBlockEnd: "24px"
        },
        ".contentBlock-linkFlex": {
            marginBlockEnd: 0,
            a: {
                marginBlockStart: 0
            }
        }
    },
    ".mobile-image-container": {
        [media.above.sm]: {
            display: "none"
        },
        marginBlockStart: "-50px",
        padding: "16px 16px 0 16px",
        position: "relative",
        zIndex: -1
    },
    ".mobile-image-container img": {
        height: "auto",
        width: "100%",
        maxWidth: "100%",
        verticalAlign: "bottom"
    }
});
export const Careers = () => {
    const intl = useIntl();

    return (
        <UFOSegment name="careers">
            <section className="careers" css={careersStyle}>
                <StyledBackground {...backgroundProperties}>
                    <Row layout="8">
                        <CTAPrimary
                            className="cta-primary"
                            eyebrowText={intl.formatMessage({
                                id: "HomePageTemplate.careersEyebrowText",
                                defaultMessage: "Careers"
                            })}
                            headerText={intl.formatMessage({
                                id: "HomePageTemplate.careersCTAHeaderText",
                                defaultMessage: "We can’t do it alone"
                            })}
                            bodyText={intl.formatMessage({
                                id: "HomePageTemplate.careersCTABodyText",
                                defaultMessage:
                                    "We have an ambitious road ahead, and we’re looking for people to join our global team to help shape the future of Atlassian."
                            })}
                            ctaProps={{
                                buttonProps: {
                                    text: intl.formatMessage({
                                        id: "HomePageTemplate.careersButtonText",
                                        defaultMessage: "Join the team"
                                    }),
                                    url: useLocalizedHref("/company/careers")
                                }
                            }}
                        />
                    </Row>
                    <div className="mobile-image-container">
                        <Image
                            loading="lazy"
                            alt={intl.formatMessage({
                                id: "HomePageTemplate.careersMobileImageAltText",
                                defaultMessage: "Atlassian Careers"
                            })}
                            src="https://wac-cdn.atlassian.com/dam/jcr:ee14953a-98f7-41d9-a2f9-21eebf69f8b9/CareersCenterMobile.svg?cdnVersion=1223"
                        />
                    </div>
                </StyledBackground>
            </section>
        </UFOSegment>
    );
};
